import { X, Plus, Minus, Check } from "lucide-react";
import { useState, useMemo } from "react";
import type { Ad } from "./ad-card";
import { AdImage } from "@/app/components/common/ad-image";
import { getAdImage } from "@/app/utils/ad-utils";

interface CounterOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalOffer: {
    buyerName: string;
    cashAmount: number;
    totalOfferValue: number;
    tradeInAds?: Array<{ id: string; value: number; title: string; image: string }>;
  };
  targetAd: Ad;
  onSubmit: (counterOffer: { 
    cashAmount: number; 
    message: string;
    selectedTradeInAdIds?: string[];
  }) => void;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

export function CounterOfferModal({
  isOpen,
  onClose,
  originalOffer,
  targetAd,
  onSubmit,
}: CounterOfferModalProps) {
  const [counterCashAmount, setCounterCashAmount] = useState(
    originalOffer.cashAmount
  );
  const [message, setMessage] = useState("");
  const [selectedTradeInAdIds, setSelectedTradeInAdIds] = useState<string[]>(
    originalOffer.tradeInAds?.map(ad => ad.id) || []
  );

  if (!isOpen) return null;

  const hasTradeInAds = originalOffer.tradeInAds && originalOffer.tradeInAds.length > 0;

  // Calculate selected trade-in value
  const selectedTradeInValue = useMemo(() => {
    if (!originalOffer.tradeInAds) return 0;
    return originalOffer.tradeInAds
      .filter(ad => selectedTradeInAdIds.includes(ad.id))
      .reduce((sum, ad) => sum + ad.value, 0);
  }, [selectedTradeInAdIds, originalOffer.tradeInAds]);

  const totalCounterOffer = selectedTradeInValue + counterCashAmount;

  const handleToggleTradeInAd = (adId: string) => {
    setSelectedTradeInAdIds(prev => {
      if (prev.includes(adId)) {
        return prev.filter(id => id !== adId);
      } else {
        return [...prev, adId];
      }
    });
  };

  const handleSubmit = () => {
    onSubmit({
      cashAmount: counterCashAmount,
      message,
      selectedTradeInAdIds: hasTradeInAds ? selectedTradeInAdIds : undefined,
    });
    onClose();
  };

  const adjustCashAmount = (delta: number) => {
    const newAmount = counterCashAmount + delta;
    if (newAmount >= 0) {
      setCounterCashAmount(newAmount);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card w-full max-w-lg mx-auto rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Fazer Contra-Oferta</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Original Offer Summary */}
          <div className="bg-accent/30 rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium">Oferta Original de {originalOffer.buyerName}:</p>
            
            {originalOffer.tradeInAds && originalOffer.tradeInAds.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground">
                    Produtos oferecidos ({selectedTradeInAdIds.length} de {originalOffer.tradeInAds.length} selecionados):
                  </p>
                  {selectedTradeInAdIds.length < originalOffer.tradeInAds.length && (
                    <button
                      onClick={() => setSelectedTradeInAdIds(originalOffer.tradeInAds?.map(ad => ad.id) || [])}
                      className="text-xs text-primary hover:underline"
                    >
                      Selecionar todos
                    </button>
                  )}
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-3 mb-3">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    💡 <strong>Dica:</strong> Você pode escolher quais produtos deseja aceitar na troca. 
                    Clique nos produtos abaixo para selecionar ou desmarcar.
                  </p>
                </div>
                <div className="space-y-2">
                  {originalOffer.tradeInAds.map((ad) => {
                    const isSelected = selectedTradeInAdIds.includes(ad.id);
                    return (
                      <button
                        key={ad.id}
                        onClick={() => handleToggleTradeInAd(ad.id)}
                        className={`w-full flex items-center gap-3 rounded-lg p-3 transition-all border-2 ${ 
                          isSelected 
                            ? 'bg-primary/10 border-primary shadow-sm' 
                            : 'bg-background border-border hover:border-primary/50'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${ 
                          isSelected 
                            ? 'bg-primary border-primary' 
                            : 'border-border bg-background'
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <AdImage
                          src={ad.image}
                          alt={ad.title}
                          className="w-12 h-12 rounded object-cover flex-shrink-0"
                        />
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium">{ad.title}</p>
                          <p className="text-sm text-primary font-semibold">{formatCurrency(ad.value)}</p>
                        </div>
                        {isSelected && (
                          <span className="text-xs bg-primary text-white px-2 py-1 rounded-full">
                            Aceito
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedTradeInAdIds.length === 0 && (
                  <div className="mt-2 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900 rounded-lg p-3">
                    <p className="text-xs text-orange-700 dark:text-orange-300">
                      ⚠️ Nenhum produto selecionado. Selecione pelo menos um produto ou ajuste apenas o valor em dinheiro.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Dinheiro oferecido:</span>
              <span className="font-medium">{formatCurrency(originalOffer.cashAmount)}</span>
            </div>
            <div className="border-t border-border pt-2 flex justify-between">
              <span className="font-medium">Total da oferta original:</span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(originalOffer.totalOfferValue)}
              </span>
            </div>
          </div>

          {/* Target Ad */}
          <div className="bg-background rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-2">Seu produto:</p>
            <div className="flex gap-3">
              <AdImage
                src={getAdImage(targetAd)}
                alt={targetAd.title}
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
              />
              <div>
                <h3 className="font-medium text-sm">{targetAd.title}</h3>
                <p className="text-lg font-semibold text-primary">{formatCurrency(targetAd.price)}</p>
              </div>
            </div>
          </div>

          {/* Counter Offer Input */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Valor em dinheiro da contra-oferta:
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => adjustCashAmount(-100)}
                  className="p-3 bg-accent hover:bg-accent/70 rounded-lg transition-colors"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <div className="flex-1">
                  <input
                    type="number"
                    value={counterCashAmount}
                    onChange={(e) => setCounterCashAmount(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-lg font-semibold text-center"
                    placeholder="0"
                    min="0"
                    step="100"
                  />
                  <p className="text-center text-xs text-muted-foreground mt-1">
                    {formatCurrency(counterCashAmount)}
                  </p>
                </div>
                <button
                  onClick={() => adjustCashAmount(100)}
                  className="p-3 bg-accent hover:bg-accent/70 rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Quick Adjustment Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => adjustCashAmount(500)}
                className="flex-1 px-3 py-2 bg-accent hover:bg-accent/70 rounded-lg text-sm transition-colors"
              >
                +R$ 500
              </button>
              <button
                onClick={() => adjustCashAmount(1000)}
                className="flex-1 px-3 py-2 bg-accent hover:bg-accent/70 rounded-lg text-sm transition-colors"
              >
                +R$ 1.000
              </button>
              <button
                onClick={() => adjustCashAmount(-500)}
                className="flex-1 px-3 py-2 bg-accent hover:bg-accent/70 rounded-lg text-sm transition-colors"
              >
                -R$ 500
              </button>
            </div>

            {/* Counter Offer Total */}
            <div className="bg-primary/10 rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-3">Resumo da Sua Contra-Oferta:</h3>
              
              {/* Selected Products Summary */}
              {hasTradeInAds && selectedTradeInAdIds.length > 0 && (
                <div className="mb-3 pb-3 border-b border-border">
                  <p className="text-xs text-muted-foreground mb-2">
                    Produtos que você aceita ({selectedTradeInAdIds.length}):
                  </p>
                  <div className="space-y-1">
                    {originalOffer.tradeInAds
                      ?.filter(ad => selectedTradeInAdIds.includes(ad.id))
                      .map(ad => (
                        <div key={ad.id} className="flex items-center gap-2 text-xs">
                          <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                          <span className="flex-1">{ad.title}</span>
                          <span className="font-medium">{formatCurrency(ad.value)}</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {selectedTradeInValue > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Valor dos produtos aceitos:</span>
                    <span className="font-medium">{formatCurrency(selectedTradeInValue)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Dinheiro solicitado:</span>
                  <span className="font-medium">{formatCurrency(counterCashAmount)}</span>
                </div>
                <div className="border-t border-border pt-2 flex justify-between">
                  <span className="font-medium">Total da contra-oferta:</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(totalCounterOffer)}
                  </span>
                </div>
              </div>
              
              {/* Difference Indicator */}
              {totalCounterOffer !== originalOffer.totalOfferValue && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    {totalCounterOffer > originalOffer.totalOfferValue ? (
                      <span className="text-green-600 dark:text-green-400 font-medium">
                        +{formatCurrency(totalCounterOffer - originalOffer.totalOfferValue)} acima da oferta original
                      </span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400 font-medium">
                        {formatCurrency(originalOffer.totalOfferValue - totalCounterOffer)} abaixo da oferta original
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Message */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Mensagem (opcional):
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg resize-none"
                rows={4}
                placeholder="Explique sua contra-oferta..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-accent hover:bg-accent/70 rounded-lg font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors"
            >
              Enviar Contra-Oferta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}